import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { defaultLanguage, localizedContent, supportedLanguages } from '../i18n/content';

export const STORAGE_KEY = 'portfolio-language';

export const normalizeLanguage = value => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized.startsWith('fr')) {
    return 'fr';
  }

  if (normalized.startsWith('en')) {
    return 'en';
  }

  return null;
};

const isSupported = value => supportedLanguages.includes(value);

export const detectBrowserLanguage = () => {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const candidates = [navigator.language, ...(navigator.languages || [])];

  for (const candidate of candidates) {
    const normalized = normalizeLanguage(candidate);
    if (isSupported(normalized)) {
      return normalized;
    }
  }

  return null;
};

const LanguageContext = createContext({
  language: defaultLanguage,
  setLanguage: () => {},
  content: localizedContent[defaultLanguage],
});

export const LanguageProvider = ({ children, initialLanguage }) => {
  const forcedLanguage = normalizeLanguage(initialLanguage);
  const [language, setLanguageState] = useState(
    isSupported(forcedLanguage) ? forcedLanguage : defaultLanguage,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isSupported(forcedLanguage)) {
      setLanguageState(forcedLanguage);
      window.localStorage.setItem(STORAGE_KEY, forcedLanguage);
      return;
    }

    const stored = normalizeLanguage(window.localStorage.getItem(STORAGE_KEY));
    if (isSupported(stored)) {
      setLanguageState(stored);
      return;
    }

    const detected = detectBrowserLanguage();
    if (isSupported(detected) && detected !== defaultLanguage) {
      setLanguageState(detected);
    }
  }, [forcedLanguage]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = nextLanguage => {
    const normalized = normalizeLanguage(nextLanguage);

    if (!isSupported(normalized)) {
      return;
    }

    setLanguageState(normalized);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    }
  };

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      content: localizedContent[language] || localizedContent[defaultLanguage],
    }),
    [language],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialLanguage: PropTypes.string,
};

LanguageProvider.defaultProps = {
  initialLanguage: null,
};

export const useLanguage = () => useContext(LanguageContext);
